import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef
} from "react";
import API from "../../api/api";
import "../../style/usage.css"

export default function Usage() {
  const [data, setData] = useState({
    chatStats: [],
    messageStats: [],
    creditUsage: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [days, setDays] = useState(7);

  const abortRef = useRef(null);

  // ✅ Safe formatter
  const format = (n) => (n ?? 0).toLocaleString();

  // ✅ Fetch with cancellation
  const fetchUsage = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      if (abortRef.current) {
        abortRef.current.abort();
      }

      const controller = new AbortController();
      abortRef.current = controller;

      const res = await API.get(`/admin/usage?days=${days}`, {
        signal: controller.signal
      });

      const response = res?.data || {};

      setData({
        chatStats: response.chatStats || [],
        messageStats: response.messageStats || [],
        creditUsage: response.creditUsage || []
      });

    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(err);
        setError("Failed to load usage data");
      }
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // ✅ Memoized totals (important)
  const totals = useMemo(() => {
    const sum = (arr, key) =>
      arr.reduce((acc, curr) => acc + (curr[key] || 0), 0);

    return {
      chats: sum(data.chatStats, "chats"),
      messages: sum(data.messageStats, "messages"),
      credits: sum(data.creditUsage, "creditsUsed")
    };
  }, [data]);

  // ✅ Empty check
  const isEmpty =
    !data.chatStats.length &&
    !data.messageStats.length &&
    !data.creditUsage.length;

  return (
    <div className="admin-page usage-page">

      {/* HEADER */}
      <div className="page-header">
        <h2 className="page-title">Usage Analytics</h2>

        <select
          className="filter-select"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value={7}>Last 7 Days</option>
          <option value={30}>Last 30 Days</option>
          <option value={90}>Last 90 Days</option>
        </select>
      </div>

      {/* ERROR */}
      {error && <div className="error">{error}</div>}

      {/* LOADING */}
      {loading && <div className="loader">Loading usage...</div>}

      {/* EMPTY */}
      {!loading && isEmpty && (
        <div className="empty">No usage data available</div>
      )}

      {!loading && !isEmpty && (
        <>
          {/* ✅ SUMMARY */}
          <div className="usage-cards">
            <div className="usage-card">
              <h4>Total Chats</h4>
              <p>{format(totals.chats)}</p>
            </div>

            <div className="usage-card">
              <h4>Total Messages</h4>
              <p>{format(totals.messages)}</p>
            </div>

            <div className="usage-card">
              <h4>Total Credits Used</h4>
              <p>{format(totals.credits)}</p>
            </div>
          </div>

          {/* ✅ TABLE COMPONENT (reusable mindset) */}
          <UsageTable
            title="Chats per Day"
            data={data.chatStats}
            field="chats"
          />

          <UsageTable
            title="Messages per Day"
            data={data.messageStats}
            field="messages"
          />

          <UsageTable
            title="Credits Used per Day"
            data={data.creditUsage}
            field="creditsUsed"
          />
        </>
      )}
    </div>
  );
}

/* ✅ Reusable Table Component */
function UsageTable({ title, data, field }) {
  const format = (n) => (n ?? 0).toLocaleString();

  return (
    <div className="usage-section">
      <h4>{title}</h4>

      <table className="table">
        <thead>
          <tr>
            <th>Date</th>
            <th>{title.split(" ")[0]}</th>
          </tr>
        </thead>

        <tbody>
          {data.map((d, i) => (
            <tr key={i}>
              <td>{d?._id || "-"}</td>
              <td>{format(d?.[field])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
